import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { api, ApiError, type Me } from '../api';
import { resetPendingApprovalPrompt, signedInHome, useAuth } from '../auth';
import { Button, Input, Alert, Card, PasswordInput, PageLoader } from '../components/ui';
import { GoogleSignIn } from '../components/GoogleSignIn';

function homeFor(user: Pick<Me, 'role' | 'needsClinicOnboarding' | 'mustChangePassword'>) {
  return signedInHome(user);
}

export function LoginPage() {
  const { me, loading: authLoading, refetch } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4 gap-3">
        <PageLoader variant="form" />
        <p className="text-sm text-ink-soft">Checking your session…</p>
      </div>
    );
  }
  if (me) return <Navigate to={homeFor(me)} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await api.post<Me>('/api/auth/login', { email, password });
      if (user.role === 'student' && user.approvalStatus === 'pending') {
        resetPendingApprovalPrompt(user.id);
      }
      await refetch();
      navigate(homeFor(user), { replace: true });
    } catch (err) {
      // Already signed in but still finishing clinic setup — send them there.
      if (
        err instanceof ApiError
        && /finish setting up your clinic|clinic onboarding/i.test(err.message)
      ) {
        await refetch();
        navigate('/clinic/onboarding', { replace: true });
        return;
      }
      setError(
        err instanceof ApiError
          ? (err.status === 401 ? 'Wrong email or password.' : err.message)
          : 'Could not sign in. Try again.',
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <Card className={`w-full max-w-sm p-8 relative ${loading ? 'pointer-events-none' : ''}`}>
        {loading && (
          <div
            className="absolute inset-0 z-10 rounded-[inherit] bg-surface/80 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3 px-6"
            role="status"
            aria-live="polite"
          >
            <span
              className="inline-block w-8 h-8 rounded-full border-[3px] border-sand border-r-transparent animate-spin"
              aria-hidden
            />
            <p className="text-sm font-medium text-ink">Signing you in…</p>
            <p className="text-xs text-ink-soft text-center">This only takes a moment.</p>
          </div>
        )}
        <h1 className="font-heading text-2xl font-bold text-ink mb-1">Welcome back</h1>
        <p className="text-sm text-ink-soft mb-6">Pick up where you left off.</p>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={loading}
          />
          <PasswordInput
            label="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            disabled={loading}
          />
          <div className="text-right">
            <Link to="/forgot-password" className="text-xs text-sand-strong hover:underline">
              Forgot password?
            </Link>
          </div>
          <Button type="submit" loading={loading} className="w-full" size="lg">
            {loading ? 'Signing in…' : 'Log in'}
          </Button>
        </form>
        {!loading && (
          <GoogleSignIn
            onError={setError}
            onUnknownAccount={({ email, fullName, credential }) => {
              sessionStorage.setItem('physioedge.googlePrefill', JSON.stringify({ email, fullName, credential }));
              navigate('/register');
            }}
          />
        )}
        <p className="text-center text-sm text-ink-soft mt-6">
          New here?{' '}
          <Link to="/register" className="text-sand-strong font-medium hover:underline">Join us — it’s free</Link>
        </p>
      </Card>
    </div>
  );
}
